import { ColorPalette } from '../types';

export const PRESET_PALETTES: ColorPalette[] = [
  {
    id: 'vibrant',
    name: '🎪 Vibrant Carnival',
    description: 'Energetic, festive primary and secondary pops',
    colors: [
      '#EF4444', // Red
      '#3B82F6', // Blue
      '#10B981', // Green
      '#F59E0B', // Amber
      '#8B5CF6', // Purple
      '#EC4899', // Pink
      '#06B6D4', // Cyan
      '#F97316', // Orange
    ],
  },
  {
    id: 'cyberpunk',
    name: '⚡ Cyberpunk Neon',
    description: 'High-voltage electric neon hues',
    colors: [
      '#FF007F', // Neon Pink
      '#00F0FF', // Electric Cyan
      '#7000FF', // Deep Purple
      '#FFE600', // Acid Yellow
      '#00FF66', // Matrix Green
      '#FF3366', // Laser Red
      '#9D00FF', // Neon Violet
      '#00E5FF', // Bright Sky
    ],
  },
  {
    id: 'emerald-luxury',
    name: '👑 Emerald & Gold Luxury',
    description: 'Regal jewel tones and rich golds',
    colors: [
      '#0F766E', // Deep Teal
      '#D97706', // Gold
      '#047857', // Emerald
      '#B45309', // Bronze
      '#0E7490', // Ocean Deep
      '#CA8A04', // Rich Yellow
      '#15803D', // Forest Green
      '#EAB308', // Bright Gold
    ],
  },
  {
    id: 'sunset',
    name: '🌅 Sunset Flare',
    description: 'Warm, radiant gradient tones',
    colors: [
      '#F43F5E', // Rose
      '#FB923C', // Orange
      '#FBBF24', // Amber
      '#E11D48', // Crimson
      '#D946EF', // Fuchsia
      '#FF70A6', // Coral
      '#FF9770', // Apricot
      '#FFD670', // Sunshine
    ],
  },
  {
    id: 'cosmic',
    name: '🌌 Cosmic Nebula',
    description: 'Deep mystical space tones',
    colors: [
      '#6366F1', // Indigo
      '#A855F7', // Purple
      '#EC4899', // Pink
      '#3B82F6', // Blue
      '#8B5CF6', // Violet
      '#D946EF', // Fuchsia
      '#4F46E5', // Deep Indigo
      '#C084FC', // Light Purple
    ],
  },
  {
    id: 'pastel',
    name: '🧁 Pastel Dream',
    description: 'Soft, delightful confectionery colors',
    colors: [
      '#FCA5A5', // Pastel Red
      '#93C5FD', // Pastel Blue
      '#86EFAC', // Pastel Green
      '#FDE047', // Pastel Yellow
      '#C4B5FD', // Pastel Purple
      '#F9A8D4', // Pastel Pink
      '#67E8F9', // Pastel Cyan
      '#FDBA74', // Pastel Orange
    ],
  },
  {
    id: 'arcade',
    name: '🕹️ 80s Retro Arcade',
    description: 'Nostalgic synthwave arcade colors',
    colors: [
      '#FF0055',
      '#00D4FF',
      '#FF9900',
      '#5500FF',
      '#00FF99',
      '#FF00AA',
      '#FFFF00',
      '#0099FF',
    ],
  },
];

/**
 * Get contrast text color (black or white) based on background hex
 */
export function getContrastTextColor(hexColor: string): '#ffffff' | '#0f172a' {
  // Clean hex
  const cleanHex = hexColor.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16) || 0;
  const g = parseInt(cleanHex.substring(2, 4), 16) || 0;
  const b = parseInt(cleanHex.substring(4, 6), 16) || 0;

  // Calculate relative luminance (W3C formula)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#0f172a' : '#ffffff';
}

/**
 * Resolve the color for a segment given its index, total segments, custom color override, and palette ID
 */
export function getSegmentColor(
  index: number,
  totalSegments: number,
  customColor?: string,
  paletteId: string = 'vibrant'
): string {
  if (customColor) return customColor;

  const palette = PRESET_PALETTES.find((p) => p.id === paletteId) || PRESET_PALETTES[0];
  const colors = palette.colors;
  const numColors = colors.length;

  if (totalSegments <= 1) return colors[0];

  // If last segment would have the same color as the first segment (due to modulo wrap),
  // pick a distinct fallback from palette to prevent adjacent matching slices
  const baseColorIndex = index % numColors;
  if (index === totalSegments - 1 && baseColorIndex === 0 && totalSegments > 1) {
    // Pick the middle color or second to last
    const altIndex = Math.floor(numColors / 2);
    return colors[altIndex] || colors[1];
  }

  return colors[baseColorIndex];
}

/**
 * Get a slightly darker/lighter shade for radial segment depth
 */
export function adjustBrightness(hex: string, percent: number): string {
  const clean = hex.replace('#', '');
  let num = parseInt(clean, 16);
  if (isNaN(num)) return hex;

  let r = (num >> 16) + Math.round(255 * (percent / 100));
  let g = ((num >> 8) & 0x00ff) + Math.round(255 * (percent / 100));
  let b = (num & 0x0000ff) + Math.round(255 * (percent / 100));

  r = Math.min(255, Math.max(0, r));
  g = Math.min(255, Math.max(0, g));
  b = Math.min(255, Math.max(0, b));

  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}
